import { visit } from "@hotwired/turbo";
import { useApiRequest } from "../../hooks/useApiRequest";

import { AvatarAppearance } from "../../types/avatar_appearances.d";

interface AvatarAppearanceIndexProps {
  avatarAppearances: AvatarAppearance[];
}

// #STYLE_GUIDE: Index component shows list with delete functionality
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
// - API calls via useApiRequest hook for consistency
export default function AvatarAppearanceIndex({ avatarAppearances }: AvatarAppearanceIndexProps) {
  const { loading, makeRequest } = useApiRequest({
    onSuccess: () => {
      // #TURBO: Use Turbo.visit for navigation instead of window.location
      visit("/avatar_appearances");
    },
    onError: (error) => {
      console.error("Failed to delete avatar_appearance:", error);
      // #TODO: Add proper error handling/toast notifications
    },
  });

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this avatar_appearance?")) {
      await makeRequest("DELETE", `/api/v1/avatar_appearances/${id}`);
    }
  };

  const handleView = (id: string) => {
    // #TURBO: Use Turbo.visit for navigation
    visit(`/avatar_appearances/${id}`);
  };

  const handleEdit = (id: string) => {
    // #TURBO: Use Turbo.visit for navigation
    visit(`/avatar_appearances/${id}/edit`);
  };

  const handleCreate = () => {
    // #TURBO: Use Turbo.visit for navigation
    visit("/avatar_appearances/new");
  };

  return (
    <div className="space-y-6">
      {/* Header with Create button */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">AvatarAppearances</h1>
        <button
          onClick={handleCreate}
          className="btn btn-primary"
        >
          Create New AvatarAppearance
        </button>
      </div>

      {/* Table using daisyUI */}
      <div className="overflow-x-auto">
        <table className="table table-zebra">
          <thead>
            <tr>
              <th>ID</th>
              <th>Avatar</th>
              <th>Name</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {avatarAppearances.map((avatarAppearance) => (
              <tr key={avatarAppearance.id}>
                <td>{avatarAppearance.id}</td>
                <td>{avatarAppearance.avatarDisplayName || avatarAppearance.avatarId || ''}</td>
                <td>{avatarAppearance.name}</td>
                <td>{new Date(avatarAppearance.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className="join">
                    <button
                      onClick={() => handleView(avatarAppearance.id)}
                      className="btn btn-sm btn-outline join-item"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleEdit(avatarAppearance.id)}
                      className="btn btn-sm btn-outline join-item"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(avatarAppearance.id)}
                      className="btn btn-sm btn-error join-item"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="loading loading-spinner loading-xs"></span>
                      ) : (
                        "Delete"
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {avatarAppearances.length === 0 && (
        <div className="text-center py-8">
          <p className="text-base-content/60">No avatar appearances found.</p>
          <button
            onClick={handleCreate}
            className="btn btn-primary mt-4"
          >
            Create Your First AvatarAppearance
          </button>
        </div>
      )}
    </div>
  );
}
